import type { PackKind, PackTier, PackVisibility } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy do catálogo de packs (ADR-0400/0423/0700/0702/0703/0704): tabela, filtros, detalhe,
   aba de versões e histórico de takedown.

   Os rótulos de enum (visibilidade/tier/kind/status de versão) NÃO estão aqui: eles moram em
   `pack-catalog-labels.ts` com um parâmetro `locale?` opcional, porque essas funções também são
   importadas por `pack-submissions/submission-overview-card.tsx` e `routes/pack-detail-page.tsx`
   — duas frentes que ainda não migraram para o padrão bilíngue. Um `Bilingual<T>` aqui exigiria
   `useBilingual` (hook), e as chamadas de lá são de fora de render tree desta área. `filterModel`
   é o vocabulário PRÓPRIO de `packs-catalog-filter-model.ts` (já divergia do primeiro em
   pt-BR — "Grátis" vs "Free" — a migração preserva a divergência existente em vez de unificar
   por conta própria). */
interface PacksCatalogContent {
  readonly filterModel: {
    readonly tier: Readonly<Record<PackTier, string>>;
    readonly kind: Readonly<Record<PackKind, string>>;
    readonly visibility: Readonly<Record<'all' | PackVisibility, string>>;
  };
  readonly detailCard: {
    readonly title: string;
    readonly description: string;
    readonly noDescription: string;
    readonly category: string;
    readonly minAppVersion: string;
    readonly publisher: string;
    readonly publisherIdLabel: string;
    readonly capabilities: string;
    readonly noCapabilities: string;
    readonly tags: string;
    readonly noTags: string;
  };
  readonly takedownHistory: {
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly reasonMissing: string;
    readonly byPrefix: string;
  };
  readonly versionsTab: {
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly columnVersion: string;
    readonly columnStatus: string;
    readonly columnPublishedAt: string;
    readonly columnPublisher: string;
    readonly columnActions: string;
    readonly firstParty: string;
    readonly takedown: string;
  };
  readonly table: {
    readonly emptyFilteredTitle: string;
    readonly emptyFilteredDescription: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly clearFilters: string;
    readonly columnPack: string;
    readonly columnAuthor: string;
    readonly columnTier: string;
    readonly columnVisibility: string;
    readonly columnActiveVersion: string;
    readonly columnVersions: string;
    readonly columnUpdated: string;
    readonly columnActions: string;
    readonly verified: string;
    readonly noActiveVersion: string;
    readonly takedown: string;
    readonly selectAllAriaLabel: string;
    readonly selectRowAriaLabel: (displayName: string, version: string) => string;
  };
  readonly filtersUi: {
    readonly searchAriaLabel: string;
    readonly searchPlaceholder: string;
    readonly visibilityAriaLabel: string;
    readonly tierLabel: string;
    readonly kindLabel: string;
    readonly verifiedAriaLabel: string;
    readonly activeAriaLabel: string;
    readonly sortAriaLabel: string;
    readonly verifiedAll: string;
    readonly verifiedOnly: string;
    readonly activeAll: string;
    readonly activeOnly: string;
    readonly sortInstalls: string;
    readonly sortName: string;
    readonly sortRecent: string;
    /** Contador "N de M packs/pack" — plural depende da contagem TOTAL, não do resultado. */
    readonly resultCount: (result: number, total: number) => string;
  };
}

const PACKS_CATALOG_CONTENT: Bilingual<PacksCatalogContent> = {
  'pt-BR': {
    filterModel: {
      tier: { free: 'Grátis', pro: 'Pro', team: 'Team', enterprise: 'Enterprise' },
      kind: { template: 'Template', extension: 'Extensão', 'module-pack': 'Module pack' },
      visibility: {
        all: 'Todas',
        public: 'Pública',
        'org-private': 'Org privada',
        byMyself: 'Pessoal',
      },
    },
    detailCard: {
      title: 'Detalhes',
      description: 'Descrição',
      noDescription: 'Sem descrição.',
      category: 'Categoria',
      minAppVersion: 'Versão mínima do app',
      publisher: 'Publisher',
      publisherIdLabel: 'ID do publisher',
      capabilities: 'Capacidades',
      noCapabilities: 'Nenhuma capacidade declarada.',
      tags: 'Tags',
      noTags: 'Sem tags.',
    },
    takedownHistory: {
      emptyTitle: 'Sem takedowns',
      emptyDescription: 'Nenhuma versão deste pack foi removida por enforcement de staff.',
      reasonMissing: 'Motivo não registrado.',
      byPrefix: 'Por',
    },
    versionsTab: {
      emptyTitle: 'Sem versões',
      emptyDescription: 'Este pack ainda não tem versões no índice.',
      columnVersion: 'Versão',
      columnStatus: 'Status',
      columnPublishedAt: 'Publicada em',
      columnPublisher: 'Publisher',
      columnActions: 'Ações',
      firstParty: 'first-party',
      takedown: 'Takedown',
    },
    table: {
      emptyFilteredTitle: 'Nenhum resultado',
      emptyFilteredDescription: 'Nenhum pack corresponde aos filtros aplicados.',
      emptyTitle: 'Nenhum pack publicado',
      emptyDescription:
        'Packs publicados no registry aparecem aqui (first-party e community aprovados).',
      clearFilters: 'Limpar filtros',
      columnPack: 'Pack',
      columnAuthor: 'Autor',
      columnTier: 'Tier',
      columnVisibility: 'Visibilidade',
      columnActiveVersion: 'Versão ativa',
      columnVersions: 'Versões',
      columnUpdated: 'Atualizado',
      columnActions: 'Ações',
      verified: 'Verificado',
      noActiveVersion: 'Sem versão ativa',
      takedown: 'Takedown',
      selectAllAriaLabel: 'Selecionar versões live visíveis',
      selectRowAriaLabel: (displayName, version) => `Selecionar ${displayName} v${version}`,
    },
    filtersUi: {
      searchAriaLabel: 'Buscar packs',
      searchPlaceholder: 'Buscar por nome ou id…',
      visibilityAriaLabel: 'Filtrar por visibilidade',
      tierLabel: 'Tier',
      kindLabel: 'Tipo',
      verifiedAriaLabel: 'Filtrar por verificação',
      activeAriaLabel: 'Filtrar por versão ativa',
      sortAriaLabel: 'Ordenar por',
      verifiedAll: 'Todos',
      verifiedOnly: 'Verificados',
      activeAll: 'Todos',
      activeOnly: 'Com versão ativa',
      sortInstalls: 'Mais instalados',
      sortName: 'Nome (A-Z)',
      sortRecent: 'Mais recentes',
      resultCount: (result, total) => `${result} de ${total} ${total === 1 ? 'pack' : 'packs'}`,
    },
  },
  'en-US': {
    filterModel: {
      tier: { free: 'Free', pro: 'Pro', team: 'Team', enterprise: 'Enterprise' },
      kind: { template: 'Template', extension: 'Extension', 'module-pack': 'Module pack' },
      visibility: {
        all: 'All',
        public: 'Public',
        'org-private': 'Org-private',
        byMyself: 'Personal',
      },
    },
    detailCard: {
      title: 'Details',
      description: 'Description',
      noDescription: 'No description.',
      category: 'Category',
      minAppVersion: 'Minimum app version',
      publisher: 'Publisher',
      publisherIdLabel: 'Publisher ID',
      capabilities: 'Capabilities',
      noCapabilities: 'No capabilities declared.',
      tags: 'Tags',
      noTags: 'No tags.',
    },
    takedownHistory: {
      emptyTitle: 'No takedowns',
      emptyDescription: 'No version of this pack was removed by staff enforcement.',
      reasonMissing: 'No reason recorded.',
      byPrefix: 'By',
    },
    versionsTab: {
      emptyTitle: 'No versions',
      emptyDescription: 'This pack has no versions in the index yet.',
      columnVersion: 'Version',
      columnStatus: 'Status',
      columnPublishedAt: 'Published at',
      columnPublisher: 'Publisher',
      columnActions: 'Actions',
      firstParty: 'first-party',
      takedown: 'Takedown',
    },
    table: {
      emptyFilteredTitle: 'No results',
      emptyFilteredDescription: 'No pack matches the applied filters.',
      emptyTitle: 'No published packs',
      emptyDescription:
        'Packs published to the registry show up here (first-party and approved community).',
      clearFilters: 'Clear filters',
      columnPack: 'Pack',
      columnAuthor: 'Author',
      columnTier: 'Tier',
      columnVisibility: 'Visibility',
      columnActiveVersion: 'Active version',
      columnVersions: 'Versions',
      columnUpdated: 'Updated',
      columnActions: 'Actions',
      verified: 'Verified',
      noActiveVersion: 'No active version',
      takedown: 'Takedown',
      selectAllAriaLabel: 'Select visible live versions',
      selectRowAriaLabel: (displayName, version) => `Select ${displayName} v${version}`,
    },
    filtersUi: {
      searchAriaLabel: 'Search packs',
      searchPlaceholder: 'Search by name or id…',
      visibilityAriaLabel: 'Filter by visibility',
      tierLabel: 'Tier',
      kindLabel: 'Kind',
      verifiedAriaLabel: 'Filter by verification',
      activeAriaLabel: 'Filter by active version',
      sortAriaLabel: 'Sort by',
      verifiedAll: 'All',
      verifiedOnly: 'Verified',
      activeAll: 'All',
      activeOnly: 'With active version',
      sortInstalls: 'Most installed',
      sortName: 'Name (A-Z)',
      sortRecent: 'Most recent',
      resultCount: (result, total) => `${result} of ${total} ${total === 1 ? 'pack' : 'packs'}`,
    },
  },
};

export const usePacksCatalogContent = (): PacksCatalogContent =>
  useBilingual(PACKS_CATALOG_CONTENT);
